import React, { useEffect, useState } from "react";
import { Mail, Send, Clock } from "lucide-react";
import { api } from "../../lib/api";

export const AdminAbandonedTab = ({ discounts = [] }) => {
  const [carts, setCarts] = useState([]);
  const [threshold, setThreshold] = useState(10);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [discountByOrder, setDiscountByOrder] = useState({});
  const [testEmail, setTestEmail] = useState("");
  const [testStatus, setTestStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/abandoned-carts", { params: { after_min: threshold } });
      setCarts(r.data.carts || []);
    } catch {
      setCarts([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threshold]);

  const sendNudge = async (order_number) => {
    setSendingId(order_number);
    try {
      const code = discountByOrder[order_number] || null;
      const r = await api.post(`/admin/abandoned-carts/${order_number}/nudge`, {
        discount_code: code,
      });
      if (!r.data.sent) {
        alert("Email not sent — check that RESEND_API_KEY is set and the sender domain is verified.");
      }
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Nudge failed");
    } finally {
      setSendingId(null);
    }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    setTestStatus("...");
    try {
      const r = await api.post("/admin/email/test", { to: testEmail });
      setTestStatus(r.data.sent ? "✓ Sent — check your inbox" : "✗ Skipped (RESEND_API_KEY not set or domain not verified)");
    } catch (err) {
      setTestStatus("✗ " + (err?.response?.data?.detail || "Failed"));
    }
  };

  return (
    <section data-testid="admin-abandoned-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ ABANDONED CARTS</p>
        <label className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          IDLE >
          <input
            type="number" min="0" value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value || 0))}
            className="w-16 bg-transparent border border-kult-border px-2 py-1 font-mono text-xs text-white"
            data-testid="abandoned-threshold"
          />
          MIN
        </label>
      </div>

      <form onSubmit={sendTest} className="border border-kult-border p-4 mb-6 flex flex-wrap gap-2 items-end" data-testid="email-test-form">
        <label className="flex-1 min-w-[200px]">
          <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">SEND TEST EMAIL TO</span>
          <input
            type="email" required value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-xs"
            data-testid="email-test-input"
          />
        </label>
        <button type="submit" className="bg-kult-yellow text-black px-4 py-2 font-mono text-[10px] tracking-[0.25em]" data-testid="email-test-submit">
          <Mail size={12} className="inline mr-1" /> SEND TEST
        </button>
        {testStatus && (
          <p className="w-full font-mono text-xs text-kult-muted" data-testid="email-test-status">{testStatus}</p>
        )}
      </form>

      {loading ? (
        <p className="font-mono text-xs text-kult-muted">LOADING...</p>
      ) : carts.length === 0 ? (
        <div className="border border-kult-border p-6 text-center" data-testid="abandoned-empty">
          <Clock size={28} className="mx-auto mb-3 text-kult-muted" />
          <p className="font-mono text-xs text-kult-muted">No abandoned carts older than {threshold} min.</p>
          <p className="font-mono text-[10px] tracking-[0.25em] text-kult-muted mt-2">
            Carts already nudged are excluded from this list.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="abandoned-list">
          {carts.map((c) => (
            <article key={c.order_number} className="border border-kult-border p-5" data-testid={`abandoned-${c.order_number}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                <div className="md:col-span-3">
                  <p className="font-mono text-[11px] tracking-[0.25em] text-kult-pink">{c.order_number}</p>
                  <p className="font-display text-xl mt-1">{c.customer_name}</p>
                  <p className="font-mono text-[11px] text-kult-muted">{c.customer_email}</p>
                </div>
                <div className="md:col-span-3 font-mono text-[11px] text-kult-muted">
                  <p>{new Date(c.created_at).toLocaleString()}</p>
                  <p className="mt-1">
                    {c.items?.length} item{c.items?.length !== 1 ? "s" : ""}
                    <span className="text-kult-yellow ml-2">€{c.total?.toFixed(2)}</span>
                  </p>
                </div>
                <div className="md:col-span-3">
                  <label className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">DISCOUNT (optional)</label>
                  <select
                    value={discountByOrder[c.order_number] || ""}
                    onChange={(e) => setDiscountByOrder({ ...discountByOrder, [c.order_number]: e.target.value })}
                    className="w-full bg-kult-black border border-kult-border px-2 py-2 font-mono text-xs"
                    data-testid={`abandoned-discount-${c.order_number}`}
                  >
                    <option value="">— no discount —</option>
                    {discounts.filter((d) => d.active).map((d) => (
                      <option key={d.code} value={d.code}>
                        {d.code} ({d.type === "percent" ? `${d.value}%` : `€${d.value}`})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3 flex justify-end">
                  <button
                    onClick={() => sendNudge(c.order_number)}
                    disabled={sendingId === c.order_number}
                    className="bg-kult-pink text-black px-4 py-2 font-mono text-[10px] tracking-[0.25em] hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid={`abandoned-nudge-${c.order_number}`}
                  >
                    <Send size={12} />
                    {sendingId === c.order_number ? "..." : "SEND NUDGE"}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};
